/**
 * EMZI NEXUS V6.4 - Live Teleprompter
 * Full-screen teleprompter for live streaming sessions
 * Features: auto-scroll, section tracking, CTA alerts, timer, keyboard controls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/liveStudioV2';

const SECTION_COLORS = {
  opening: { bg: '#0a2e14', border: '#28a745', label: 'PEMBUKAAN' },
  product_story: { bg: '#0a1e3d', border: '#0e67a2', label: 'CERITA PRODUK' },
  engagement: { bg: '#1a1040', border: '#667eea', label: 'ENGAGEMENT' },
  objection_handling: { bg: '#2d1a00', border: '#fd7e14', label: 'HANDLE BANTAHAN' },
  closing: { bg: '#2d0a0a', border: '#dc3545', label: 'PENUTUP' },
  cta: { bg: '#2d1f00', border: '#F89B29', label: 'CTA' }
};

const Teleprompter = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  // State
  const [teleData, setTeleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(2);
  const [fontSize, setFontSize] = useState(32);
  const [elapsed, setElapsed] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const scrollRef = useRef(null);
  const timerRef = useRef(null);
  const autoScrollRef = useRef(null);
  const sectionRefs = useRef([]);
  const controlsTimeoutRef = useRef(null);

  // Load teleprompter data
  useEffect(() => {
    if (!sessionId || !token) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await api.getTeleprompterData(token, sessionId);
        setTeleData(data);
        sectionRefs.current = new Array(data.sections.length).fill(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [sessionId, token]);

  // Timer
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying]);

  // Auto-scroll
  useEffect(() => {
    if (isPlaying && scrollRef.current) {
      autoScrollRef.current = setInterval(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop += scrollSpeed;
        }
      }, 50);
    }
    return () => clearInterval(autoScrollRef.current);
  }, [isPlaying, scrollSpeed]);

  // Hide controls after inactivity
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    const handleMouseMove = () => resetControlsTimeout();
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(controlsTimeoutRef.current);
    };
  }, [resetControlsTimeout]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          setIsPlaying(prev => !prev);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setScrollSpeed(prev => Math.min(prev + 0.5, 8));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setScrollSpeed(prev => Math.max(prev - 0.5, 0.5));
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToSection(Math.min(activeIndex + 1, (teleData?.sections?.length || 1) - 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToSection(Math.max(activeIndex - 1, 0));
          break;
        case '+':
        case '=':
          setFontSize(prev => Math.min(prev + 4, 64));
          break;
        case '-':
          setFontSize(prev => Math.max(prev - 4, 16));
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'Escape':
          if (isFullscreen) toggleFullscreen();
          break;
        default:
          break;
      }
      resetControlsTimeout();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, teleData, isFullscreen, resetControlsTimeout]);

  const goToSection = (index) => {
    setActiveIndex(index);
    const el = sectionRefs.current[index];
    if (el && scrollRef.current) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const markSection = async (sectionId, status) => {
    try {
      await api.updateTeleprompterProgress(token, sessionId, sectionId, status);
      setTeleData(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          s.section_id === sectionId ? { ...s, status } : s
        ),
        completed_sections: prev.sections.filter(s =>
          s.section_id === sectionId ? status === 'completed' : s.status === 'completed'
        ).length
      }));
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getSectionStyle = (type) => SECTION_COLORS[type] || { bg: '#1a1a2e', border: '#555', label: type?.toUpperCase() };

  if (loading) {
    return (
      <div data-testid="teleprompter-loading" style={{ height: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 1.5s infinite' }}>Loading...</div>
          <p style={{ color: '#888' }}>Memuat teleprompter...</p>
        </div>
      </div>
    );
  }

  if (error || !teleData) {
    return (
      <div data-testid="teleprompter-error" style={{ height: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#fff', textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>!</div>
          <h2 style={{ marginBottom: '8px' }}>Teleprompter Error</h2>
          <p style={{ color: '#f66', marginBottom: '24px' }}>{error || 'No data available'}</p>
          <button
            data-testid="teleprompter-back-btn"
            onClick={() => navigate(-1)}
            style={{ padding: '12px 32px', background: '#0e67a2', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '16px', cursor: 'pointer' }}
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  const sections = teleData.sections || [];
  const completedCount = sections.filter(s => s.status === 'completed').length;
  const progressPct = sections.length > 0 ? Math.round((completedCount / sections.length) * 100) : 0;

  return (
    <div data-testid="teleprompter-container" style={{ height: '100vh', background: '#0a0a1a', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Top bar */}
      <div
        data-testid="teleprompter-header"
        style={{
          background: 'rgba(10,10,26,0.95)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          padding: '10px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s',
          zIndex: 20
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            data-testid="teleprompter-exit-btn"
            onClick={() => navigate(-1)}
            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '6px', padding: '6px 14px', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}
          >
            Exit
          </button>
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>
            {teleData.platform} Teleprompter
          </span>
          <span style={{ color: '#666', fontSize: '12px' }}>
            {teleData.duration_minutes}min
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {/* Timer */}
          <div data-testid="teleprompter-timer" style={{ color: isPlaying ? '#4ade80' : '#888', fontFamily: 'monospace', fontSize: '20px', fontWeight: 700 }}>
            {formatTime(elapsed)}
          </div>

          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '100px', height: '4px', background: '#333', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${progressPct}%`, height: '100%', background: '#4ade80', transition: 'width 0.3s' }} />
            </div>
            <span style={{ color: '#888', fontSize: '12px' }}>{completedCount}/{sections.length}</span>
          </div>
        </div>
      </div>

      {/* Main script area */}
      <div
        ref={scrollRef}
        data-testid="teleprompter-scroll-area"
        onClick={() => resetControlsTimeout()}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '40px 20px 120px',
          scrollBehavior: 'smooth'
        }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {sections.map((section, idx) => {
            const style = getSectionStyle(section.type);
            const isActive = idx === activeIndex;
            const isDone = section.status === 'completed';
            const isSkipped = section.status === 'skipped';

            return (
              <div
                key={section.section_id}
                ref={el => sectionRefs.current[idx] = el}
                data-testid={`teleprompter-section-${idx}`}
                onClick={() => setActiveIndex(idx)}
                style={{
                  background: isActive ? style.bg : 'rgba(255,255,255,0.02)',
                  borderLeft: `4px solid ${isActive ? style.border : isDone ? '#4ade80' : isSkipped ? '#888' : '#333'}`,
                  borderRadius: '8px',
                  padding: '24px 28px',
                  marginBottom: '12px',
                  cursor: 'pointer',
                  opacity: isDone || isSkipped ? 0.5 : 1,
                  transition: 'all 0.3s',
                  position: 'relative'
                }}
              >
                {/* Section header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: style.border,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      background: `${style.border}22`,
                      padding: '3px 8px',
                      borderRadius: '4px'
                    }}>
                      {section.is_cta ? 'CTA' : style.label}
                    </span>
                    {isDone && <span style={{ fontSize: '11px', color: '#4ade80' }}>Done</span>}
                    {isSkipped && <span style={{ fontSize: '11px', color: '#888' }}>Skipped</span>}
                  </div>
                  <span style={{ fontSize: '12px', color: '#555', fontFamily: 'monospace' }}>
                    {section.time}
                  </span>
                </div>

                {/* Content */}
                <p style={{
                  fontSize: `${fontSize}px`,
                  lineHeight: 1.6,
                  color: isActive ? '#fff' : '#aaa',
                  margin: 0,
                  fontWeight: isActive ? 500 : 400,
                  transition: 'color 0.3s'
                }}>
                  {section.content}
                </p>

                {/* Notes */}
                {section.notes && isActive && (
                  <p style={{ fontSize: '14px', color: '#888', marginTop: '12px', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '10px' }}>
                    {section.notes}
                  </p>
                )}

                {/* Quick actions on active */}
                {isActive && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button
                      data-testid={`section-${idx}-complete-btn`}
                      onClick={(e) => { e.stopPropagation(); markSection(section.section_id, 'completed'); if (idx < sections.length - 1) goToSection(idx + 1); }}
                      style={{ padding: '6px 16px', background: '#166534', border: '1px solid #22c55e33', borderRadius: '6px', color: '#4ade80', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Done
                    </button>
                    <button
                      data-testid={`section-${idx}-skip-btn`}
                      onClick={(e) => { e.stopPropagation(); markSection(section.section_id, 'skipped'); if (idx < sections.length - 1) goToSection(idx + 1); }}
                      style={{ padding: '6px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#888', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Skip
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom control bar */}
      <div
        data-testid="teleprompter-controls"
        style={{
          background: 'rgba(10,10,26,0.97)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '24px',
          flexShrink: 0,
          opacity: showControls ? 1 : 0.15,
          transition: 'opacity 0.3s',
          zIndex: 20
        }}
        onMouseEnter={() => setShowControls(true)}
      >
        {/* Font size */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            data-testid="font-decrease-btn"
            onClick={() => setFontSize(prev => Math.max(prev - 4, 16))}
            style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#aaa', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            A-
          </button>
          <span style={{ color: '#666', fontSize: '11px', width: '32px', textAlign: 'center' }}>{fontSize}</span>
          <button
            data-testid="font-increase-btn"
            onClick={() => setFontSize(prev => Math.min(prev + 4, 64))}
            style={{ width: '32px', height: '32px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#aaa', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            A+
          </button>
        </div>

        {/* Scroll speed */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#555', fontSize: '11px' }}>Speed</span>
          <input
            data-testid="scroll-speed-slider"
            type="range"
            min="0.5"
            max="8"
            step="0.5"
            value={scrollSpeed}
            onChange={(e) => setScrollSpeed(parseFloat(e.target.value))}
            style={{ width: '80px', accentColor: '#0e67a2' }}
          />
          <span style={{ color: '#888', fontSize: '11px', width: '24px' }}>{scrollSpeed}x</span>
        </div>

        {/* Play/Pause */}
        <button
          data-testid="play-pause-btn"
          onClick={() => setIsPlaying(prev => !prev)}
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: isPlaying ? '#dc3545' : '#0e67a2',
            border: 'none',
            color: '#fff',
            fontSize: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isPlaying ? '0 0 20px rgba(220,53,69,0.3)' : '0 0 20px rgba(14,103,162,0.3)',
            transition: 'all 0.2s'
          }}
        >
          {isPlaying ? '||' : '\u25B6'}
        </button>

        {/* Nav */}
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            data-testid="prev-section-btn"
            onClick={() => goToSection(Math.max(activeIndex - 1, 0))}
            disabled={activeIndex === 0}
            style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: activeIndex === 0 ? '#444' : '#aaa', fontSize: '13px', cursor: activeIndex === 0 ? 'default' : 'pointer' }}
          >
            Prev
          </button>
          <button
            data-testid="next-section-btn"
            onClick={() => goToSection(Math.min(activeIndex + 1, sections.length - 1))}
            disabled={activeIndex >= sections.length - 1}
            style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: activeIndex >= sections.length - 1 ? '#444' : '#aaa', fontSize: '13px', cursor: activeIndex >= sections.length - 1 ? 'default' : 'pointer' }}
          >
            Next
          </button>
        </div>

        {/* Fullscreen */}
        <button
          data-testid="fullscreen-btn"
          onClick={toggleFullscreen}
          style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#aaa', fontSize: '12px', cursor: 'pointer' }}
        >
          {isFullscreen ? 'Exit FS' : 'Fullscreen'}
        </button>
      </div>

      {/* CTA Flash overlay */}
      {sections[activeIndex]?.is_cta && isPlaying && (
        <div
          data-testid="cta-alert-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '4px solid #F89B29',
            pointerEvents: 'none',
            zIndex: 10,
            animation: 'ctaPulse 1.5s infinite'
          }}
        />
      )}

      <style>{`
        @keyframes ctaPulse {
          0%, 100% { border-color: rgba(248,155,41,0.3); }
          50% { border-color: rgba(248,155,41,0.8); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        div::-webkit-scrollbar { width: 4px; }
        div::-webkit-scrollbar-track { background: transparent; }
        div::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
      `}</style>
    </div>
  );
};

export default Teleprompter;
